import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";

import { PatientNotesTableContent } from "../../../../@types/Patient";
import { ReactComponent as NoNotesImage } from "../../../../assets/images/woman-computer.svg";
import { InfiniteTable } from "../../../../components/InfiniteTable";
import { RetryButton } from "../../../../components/RetryButton";
import { Table } from "../../../../components/Table";
import { TableSkeleton } from "../../../../components/TableSkeleton";
import { invalidateAllNoteCaches } from "../../../../hooks/mutations/notes";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { desktopTableColumns } from "../NotesSection/patientNotesTableColumns";
import { CreateNoteModal } from "./components/CreateNoteModal";
import { ViewNoteModal } from "./components/ViewNoteModal";
import {
  ImageContainer,
  ManageNotesTitle,
  NoContentMessage,
  NotesTableContainer,
  NotesTableInnerContainer,
} from "./PatientNotesTable.style";

type PatientNotesTableProps = {
  patientId: number;
  content: PatientNotesTableContent[];
  isLoading?: boolean;
  isError?: boolean;
  highlightedRows: boolean[];
  highlightColor?: string | string[];
  refreshTable: () => void;
  handleEditNote: (noteId: number) => void;
  isDropDownOpen?: boolean;
  showAddNoteButton: boolean;
  // Infinite scroll props (optional for backward compatibility)
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  useInfiniteScroll?: boolean;
};

export function PatientNotesTable({
  patientId,
  isLoading,
  isError,
  content,
  highlightedRows,
  highlightColor,
  refreshTable,
  handleEditNote,
  showAddNoteButton,
  isDropDownOpen,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  useInfiniteScroll = false,
}: PatientNotesTableProps) {
  const { t } = useTranslation();

  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const handleCreateNoteClose = () => {
    // Invalidate every notes/tasks cache so the new note shows up in the
    // patient page AND in My Tasks (if it was created as a task). Without
    // this, the My Tasks list stays stale on refetchOnMount: false.
    invalidateAllNoteCaches(queryClient);
    refreshTable();
    closeModal();
  };

  const handleCloseModal = (
    notificationMessage: string,
    notificationType: "success" | "warning" | "error"
  ) => {
    if (notificationMessage && notificationType) {
      showToast(notificationMessage, notificationType);
    }

    closeModal();
  };

  const handleNewNote = () => {
    openModal(
      <CreateNoteModal
        patientId={patientId}
        setNotification={handleCloseModal}
        onClose={handleCreateNoteClose}
      />,
      {
        width: "auto",
        height: "auto",
        showCloseButton: true,
      }
    );
  };

  const handleOpenEditModal = (noteId: number) => {
    // Pass back to parent
    handleEditNote(noteId);
  };

  const handleRowClick = (patientNoteContent: PatientNotesTableContent) => {
    if (isDropDownOpen) {
      // Do not open the view notes modal when the dropdown (three dots) is open with options
      return;
    }
    openModal(
      <ViewNoteModal
        noteId={patientNoteContent.id}
        onEditClick={handleOpenEditModal}
      />,
      {
        width: "auto",
        height: "auto",
        showCloseButton: true,
      }
    );
  };

  if (isLoading) {
    return (
      <NotesTableContainer style={{ overflow: "hidden" }}>
        <NotesTableInnerContainer style={{ overflow: "hidden" }}>
          <TableSkeleton columnsNumber={4} rowsNumber={8} />
        </NotesTableInnerContainer>
      </NotesTableContainer>
    );
  }

  if (isError) {
    return (
      <ImageContainer>
        <p>
          <Trans
            i18nKey="patientPage_notes_loadError"
            components={{
              retry: <RetryButton onClick={refreshTable} />,
            }}
          />
        </p>
      </ImageContainer>
    );
  }

  if (content && content.length > 0) {
    // Use InfiniteTable when infinite scroll is enabled
    if (useInfiniteScroll && fetchNextPage) {
      return (
        <>
          {showAddNoteButton && (
            <ManageNotesTitle onClick={handleNewNote}>
              {t("patientPage_notes_addNote")}
            </ManageNotesTitle>
          )}
          <NotesTableContainer>
            <NotesTableInnerContainer>
              <InfiniteTable
                data={content}
                columns={desktopTableColumns}
                onRowClick={handleRowClick}
                highlightedRows={highlightedRows}
                highlightColor={highlightColor}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                skeletonColumns={4}
                skeletonRows={8}
              />
            </NotesTableInnerContainer>
          </NotesTableContainer>
        </>
      );
    }

    // Default to regular Table for backward compatibility
    return (
      <>
        {showAddNoteButton && (
          <ManageNotesTitle onClick={handleNewNote}>
            {t("patientPage_notesTable_addNote")}
          </ManageNotesTitle>
        )}
        <NotesTableContainer>
          <NotesTableInnerContainer>
            <Table
              data={content}
              columns={desktopTableColumns}
              onRowClick={handleRowClick}
              highlightedRows={highlightedRows}
              highlightColor={highlightColor}
            />
          </NotesTableInnerContainer>
        </NotesTableContainer>
      </>
    );
  }

  return (
    <>
      {showAddNoteButton && (
        <ManageNotesTitle onClick={handleNewNote}>
          {t("patientPage_notes_addNote")}
        </ManageNotesTitle>
      )}
      <ImageContainer>
        <NoNotesImage width={181} height={158} />
        <NoContentMessage>{t("patientPage_notes_empty")}</NoContentMessage>
      </ImageContainer>
    </>
  );
}
